/**
 * The two screens that exist because a person is not signed in, or is signed in
 * and not yet allowed in.
 *
 * Both are deliberately plain. BRAND.md §7: "name the form, give the date,
 * state the consequence" — the equivalent here is saying exactly what has
 * happened and exactly what the next step is, rather than an apologetic
 * "Access denied".
 */

import type { AuthState } from '../api'

export function SignInPage() {
  return (
    <Centred>
      <img src="/eq/eq-stacked.png" alt="Equilibrium" className="mb-8 h-20 w-auto" />
      <h1 className="text-[28px]">Equilibrium Practice</h1>
      <p className="mt-2 text-[14px] text-eq-neutral">
        Sign in with your Equilibrium account to continue.
      </p>
      {/* A plain link, not a fetch: this is a full-page navigation to Cognito's
          Hosted UI and back, and the server needs to set the PKCE cookie on the
          way out. `returnTo` brings the person back to the page they wanted. */}
      <a
        className="eq-btn-primary mt-6 no-underline"
        href={`/api/auth/login?returnTo=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`}
      >
        Sign in
      </a>
    </Centred>
  )
}

const PRODUCT_LABEL: Record<string, string> = { accounts: 'EQ Accounts', payroll: 'EQ Payroll' }

export function AwaitingAccessPage({ user }: { user: NonNullable<AuthState['user']> }) {
  // R18: invitations to every product are taken up by signing in HERE, so
  // someone invited to Accounts or Payroll only lands on this page once. Tell
  // them it worked, rather than that they are waiting.
  const others = (user.otherProducts ?? []).map((p) => PRODUCT_LABEL[p] ?? p)
  if (others.length > 0) {
    return (
      <Centred>
        <img src="/eq/eq-stacked.png" alt="Equilibrium" className="mb-8 h-20 w-auto" />
        <h1 className="text-[28px]">You are set up</h1>
        <p className="mt-3 max-w-[34rem] text-[14px]">
          Signed in as <strong>{user.email}</strong>. You have access to {others.join(' and ')} — open{' '}
          {others.length === 1 ? 'it' : 'them'} from the usual address.
        </p>
        <p className="mt-3 max-w-[34rem] text-[12.5px] text-eq-helper">
          You do not have access to EQ Practice itself. If you need it, ask a manager in your practice.
        </p>
        <a className="eq-btn-secondary mt-6 no-underline" href="/api/auth/logout">
          Sign out
        </a>
      </Centred>
    )
  }
  return (
    <Centred>
      <img src="/eq/eq-stacked.png" alt="Equilibrium" className="mb-8 h-20 w-auto" />
      <h1 className="text-[28px]">Not yet granted access</h1>
      <p className="mt-3 max-w-[34rem] text-[14px]">
        You are signed in as <strong>{user.email}</strong>, but this account has not been
        granted access to EQ Practice yet.
      </p>
      <p className="mt-3 max-w-[34rem] text-[12.5px] text-eq-helper">
        Signing in creates the account; a manager still has to activate it. Ask whoever
        administers EQ Practice to do that, then reload this page.
      </p>
      <a className="eq-btn-secondary mt-6 no-underline" href="/api/auth/logout">
        Sign out
      </a>
    </Centred>
  )
}

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-eq-grey px-8 text-center">
      {children}
    </div>
  )
}
